/**
 * Lead magnets — content-upgrade assets offered on matching blog articles.
 * Config-driven: a post's category/tags are matched to decide which magnet
 * (if any) to show.
 *
 * Matching strategy differs per magnet on purpose:
 *  - PG & Hostel Setup Kit: the mothership CMS now has a dedicated "PG &
 *    Hostels" category (split out from "Hotels" 2026-07-20), so category is
 *    a reliable broad signal — any PG/Hostel post gets the kit, even ones
 *    whose specific tags aren't in TAG_TO_MAGNET yet.
 *  - Hotel PMS Switching Kit: "Hotels" is still a broad category covering
 *    many unrelated angles (GST, UPI, comparisons, RBAC...), not just
 *    switching/migration content, so it stays tag-scoped to the posts that
 *    are actually about switching — category alone would over-match.
 */
export interface LeadMagnet {
  slug: string;
  title: string;
  description: string;
  ctaLabel: string;
  pdfUrl: string;
}

export const LEAD_MAGNETS: Record<string, LeadMagnet> = {
  "hotel-switching-kit": {
    slug: "hotel-switching-kit",
    title: "The Hotel PMS Switching Kit",
    description:
      "The exact parallel-run playbook, pre-switch checklist, and day-one role worksheet — switch hotel software without a single night of broken reservations.",
    ctaLabel: "Get the Free Switching Kit",
    pdfUrl: "/lead-magnets/hotel-switching-kit.pdf",
  },
  "pg-hostel-setup-kit": {
    slug: "pg-hostel-setup-kit",
    title: "The PG & Hostel Setup Kit",
    description:
      "The bed-level billing system, the deposit ledger, and the honest line on what's automated and what your CA still checks — everything before you outgrow the spreadsheet.",
    ctaLabel: "Get the Free Setup Kit",
    pdfUrl: "/lead-magnets/pg-hostel-setup-kit.pdf",
  },
};

// Category (lowercased, trimmed) → magnet slug. Only used where a category
// is specific enough to be a reliable signal on its own — see note above.
const CATEGORY_TO_MAGNET: Record<string, string> = {
  "pg & hostels": "pg-hostel-setup-kit",
};

// Distinctive tag (lowercased, trimmed) → magnet slug. Add entries here as
// new magnets ship — no code changes needed elsewhere.
const TAG_TO_MAGNET: Record<string, string> = {
  // Hotel PMS switching/migration content
  "how to switch hotel pms": "hotel-switching-kit",
  "hotel pms migration": "hotel-switching-kit",
  "change hotel pms": "hotel-switching-kit",
  "hotel software switching": "hotel-switching-kit",
  "hotel pms onboarding": "hotel-switching-kit",
  "hotel software setup": "hotel-switching-kit",

  // PG/Hostel content
  "pg management software": "pg-hostel-setup-kit",
  "hostel management software india": "pg-hostel-setup-kit",
  "hostel management software": "pg-hostel-setup-kit",
  "co-living software india": "pg-hostel-setup-kit",
  "pg billing software": "pg-hostel-setup-kit",
  "hostel management system": "pg-hostel-setup-kit",
  "paying guest software": "pg-hostel-setup-kit",
  "pg rent management software": "pg-hostel-setup-kit",
  "hostel billing software": "pg-hostel-setup-kit",
  "pg security deposit tracking": "pg-hostel-setup-kit",
  "pg accounting software": "pg-hostel-setup-kit",
  "bed level billing": "pg-hostel-setup-kit",
  "pg online booking": "pg-hostel-setup-kit",
  "hostel bed booking software": "pg-hostel-setup-kit",
  "pg self service booking": "pg-hostel-setup-kit",
  "hostel online payment": "pg-hostel-setup-kit",
  "pg reservation software": "pg-hostel-setup-kit",
  "pg gst exemption": "pg-hostel-setup-kit",
  "hostel accounting": "pg-hostel-setup-kit",
  "pg property p&l": "pg-hostel-setup-kit",
  "accommodation management": "pg-hostel-setup-kit",
};

export const getMagnetForPost = (
  category?: string | null,
  tags?: string[] | null
): LeadMagnet | null => {
  const tagCandidates = (tags || [])
    .filter((t): t is string => !!t)
    .map((t) => t.toLowerCase().trim());

  for (const candidate of tagCandidates) {
    const slug = TAG_TO_MAGNET[candidate];
    if (slug && LEAD_MAGNETS[slug]) return LEAD_MAGNETS[slug];
  }

  if (category) {
    const slug = CATEGORY_TO_MAGNET[category.toLowerCase().trim()];
    if (slug && LEAD_MAGNETS[slug]) return LEAD_MAGNETS[slug];
  }

  return null;
};
